import { fetchAwsApi, getApiErrorMessage, isApiResponseOk, parseApiResponse } from '../security';

const SITE_SETTINGS_ENDPOINT = '/api/manage_site_settings';

export interface SiteSettings {
  discountMin: number;
  siteDiscount: number;
  freeCredits: number;
  freeBodyScans: number;
  pricePerImage: number;
  pricePerVideoSecond: number;
  pricePerAvatar: number;
  pricePerAiMeetingMinute: number;
  pricePerBodyScan: number;
  pricePerRealtimeVideoSecond: number;
}

let siteSettingsRequest: Promise<SiteSettings> | null = null;

export interface SiteDiscountResolution {
  isEligible: boolean;
  discountAmount: number;
  totalAfterDiscount: number;
}

const DEFAULT_SITE_SETTINGS: SiteSettings = {
  discountMin: 0,
  siteDiscount: 0,
  freeCredits: 0,
  freeBodyScans: 0,
  pricePerImage: 0,
  pricePerVideoSecond: 0,
  pricePerAvatar: 0,
  pricePerAiMeetingMinute: 0,
  pricePerBodyScan: 0,
  pricePerRealtimeVideoSecond: 0,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function roundMoney(value: number) {
  return Number(value.toFixed(2));
}

function normalizeInteger(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const parsedValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsedValue)) {
    return min;
  }

  const normalized = Math.trunc(parsedValue);
  return Math.min(Math.max(normalized, min), max);
}

function normalizeNumber(value: unknown, min = 0) {
  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? Math.max(parsedValue, min) : min;
}

function normalizeSiteSettingsRecord(record: Record<string, unknown>): SiteSettings {
  return {
    discountMin: normalizeInteger(
      record.discount_min ?? record.discountMin ?? record.minimum_order_discount ?? record.minimumOrderDiscount
    ),
    siteDiscount: normalizeInteger(
      record.site_discount ?? record.siteDiscount ?? record.discount_percentage ?? record.discountPercentage,
      0,
      100
    ),
    freeCredits: normalizeInteger(record.free_credits ?? record.freeCredits),
    freeBodyScans: normalizeInteger(record.free_body_scans ?? record.freeBodyScans),
    pricePerImage: normalizeInteger(record.price_per_image ?? record.pricePerImage),
    pricePerVideoSecond: normalizeInteger(
      record.price_per_video_second
      ?? record.pricePerVideoSecond
      ?? record.price_per_video
      ?? record.pricePerVideo
    ),
    pricePerAvatar: normalizeInteger(record.price_per_avatar ?? record.pricePerAvatar),
    pricePerAiMeetingMinute: normalizeInteger(record.price_per_ai_meeting_minute ?? record.pricePerAiMeetingMinute),
    pricePerBodyScan: normalizeInteger(record.price_per_body_scan ?? record.pricePerBodyScan),
    pricePerRealtimeVideoSecond: normalizeNumber(
      record.price_per_realtime_video_second ?? record.pricePerRealtimeVideoSecond
    ),
  };
}

function parseJsonString(value: string) {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

export function parseSiteSettingsPayload(payload: unknown): SiteSettings {
  if (typeof payload === 'string') {
    return parseSiteSettingsPayload(parseJsonString(payload));
  }

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const parsedSettings = parseSiteSettingsPayload(entry);
      if (parsedSettings !== DEFAULT_SITE_SETTINGS) {
        return parsedSettings;
      }
    }

    return DEFAULT_SITE_SETTINGS;
  }

  if (!isRecord(payload)) {
    return DEFAULT_SITE_SETTINGS;
  }

  const candidates: unknown[] = [
    payload,
    payload.site_settings,
    payload.siteSettings,
    payload.settings,
    payload.data,
    payload.result,
    payload.body,
    isRecord(payload.data) ? payload.data.site_settings : undefined,
    isRecord(payload.data) ? payload.data.siteSettings : undefined,
    isRecord(payload.data) ? payload.data.settings : undefined,
    isRecord(payload.data) ? payload.data.body : undefined,
    isRecord(payload.body) ? payload.body.site_settings : undefined,
    isRecord(payload.body) ? payload.body.siteSettings : undefined,
    isRecord(payload.body) ? payload.body.settings : undefined,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' || Array.isArray(candidate)) {
      const parsedSettings = parseSiteSettingsPayload(candidate);
      if (parsedSettings !== DEFAULT_SITE_SETTINGS) {
        return parsedSettings;
      }
    }

    if (!isRecord(candidate)) {
      continue;
    }

    if (
      candidate.discount_min !== undefined ||
      candidate.discountMin !== undefined ||
      candidate.site_discount !== undefined ||
      candidate.siteDiscount !== undefined ||
      candidate.free_credits !== undefined ||
      candidate.freeCredits !== undefined ||
      candidate.free_body_scans !== undefined ||
      candidate.freeBodyScans !== undefined ||
      candidate.price_per_image !== undefined ||
      candidate.pricePerImage !== undefined ||
      candidate.price_per_video_second !== undefined ||
      candidate.pricePerVideoSecond !== undefined ||
      candidate.price_per_video !== undefined ||
      candidate.pricePerVideo !== undefined ||
      candidate.price_per_avatar !== undefined ||
      candidate.pricePerAvatar !== undefined ||
      candidate.price_per_ai_meeting_minute !== undefined ||
      candidate.pricePerAiMeetingMinute !== undefined ||
      candidate.price_per_body_scan !== undefined ||
      candidate.pricePerBodyScan !== undefined ||
      candidate.price_per_realtime_video_second !== undefined ||
      candidate.pricePerRealtimeVideoSecond !== undefined
    ) {
      return normalizeSiteSettingsRecord(candidate);
    }
  }

  return DEFAULT_SITE_SETTINGS;
}

export function resolveSiteOrderDiscount(subtotal: number, settings: SiteSettings): SiteDiscountResolution {
  const normalizedSubtotal = Number.isFinite(subtotal) && subtotal > 0 ? roundMoney(subtotal) : 0;
  const isEligible =
    settings.siteDiscount > 0 &&
    normalizedSubtotal >= settings.discountMin;
  const discountAmount = isEligible
    ? roundMoney(normalizedSubtotal * (settings.siteDiscount / 100))
    : 0;

  return {
    isEligible,
    discountAmount,
    totalAfterDiscount: roundMoney(Math.max(0, normalizedSubtotal - discountAmount)),
  };
}

export function fetchSiteSettings() {
  if (siteSettingsRequest) {
    return siteSettingsRequest;
  }

  siteSettingsRequest = (async () => {
    const response = await fetchAwsApi(SITE_SETTINGS_ENDPOINT, {
      method: 'GET',
    });
    const payload = await parseApiResponse(response);

    if (!isApiResponseOk(response, payload)) {
      throw new Error(getApiErrorMessage(payload, 'Unable to load site settings.'));
    }

    return parseSiteSettingsPayload(payload);
  })().finally(() => {
    siteSettingsRequest = null;
  });

  return siteSettingsRequest;
}

export async function updateSiteSettings(settings: SiteSettings) {
  const response = await fetchAwsApi(SITE_SETTINGS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      discount_min: normalizeInteger(settings.discountMin),
      site_discount: normalizeInteger(settings.siteDiscount, 0, 100),
      free_credits: normalizeInteger(settings.freeCredits),
      free_body_scans: normalizeInteger(settings.freeBodyScans),
      price_per_image: normalizeInteger(settings.pricePerImage),
      price_per_video_second: normalizeInteger(settings.pricePerVideoSecond),
      // Keep the legacy key in sync so existing backends storing per-video
      // credits continue to receive the (now per-second) value.
      price_per_video: normalizeInteger(settings.pricePerVideoSecond),
      price_per_avatar: normalizeInteger(settings.pricePerAvatar),
      price_per_ai_meeting_minute: normalizeInteger(settings.pricePerAiMeetingMinute),
      price_per_body_scan: normalizeInteger(settings.pricePerBodyScan),
      price_per_realtime_video_second: normalizeNumber(settings.pricePerRealtimeVideoSecond),
    }),
  });
  const payload = await parseApiResponse(response);

  if (!isApiResponseOk(response, payload)) {
    throw new Error(getApiErrorMessage(payload, 'Unable to update site settings.'));
  }

  const updatedSettings = parseSiteSettingsPayload(payload);
  // Some backends acknowledge the save without returning the saved settings.
  return updatedSettings === DEFAULT_SITE_SETTINGS
    ? normalizeSiteSettingsRecord({ ...settings })
    : updatedSettings;
}
